import { Download, Eye } from "lucide-react";
import { type AnalyticsRunOutputSummary } from "../../../../../models/audit/analyticsWorkflow";
import { Button } from "../../../../../components/ui/button";
import { cn } from "../../../../../lib/utils";
import {
  OUTPUT_FILE_NEEDS_A_ROW,
  viewableOutputFileType,
} from "../../../../../hooks/audit/useAnalyticsOutputFiles";
import { PaneSection } from "./PaneSection";

interface OutputFilesSectionProps {
  /** The files the run published, as the manifest read names them. */
  outputs: AnalyticsRunOutputSummary[];
  /**
   * True where the manifest read failed, so the empty list above is this build's
   * and not the run's.
   */
  unreadable: boolean;
  /**
   * Mints the signed URL and opens the file in Midship's viewer. `trigger` is
   * the pressed button, which takes focus back when the viewer closes.
   */
  onView: (output: AnalyticsRunOutputSummary, trigger: HTMLElement) => void;
  /** Mints the signed URL and hands the file over. */
  onDownload: (output: AnalyticsRunOutputSummary) => void;
  /** File ids whose URL is being minted. */
  pending: ReadonlySet<string>;
  /**
   * Whether this pane names the sample row the URL is minted against.
   *
   * False over a whole column, where every view and download is refused before
   * it is issued.
   */
  fetchable: boolean;
  /** The file open in the viewer, marked in its row. */
  selectedFileId?: string | null;
}

/**
 * The files the run published, each with the sentence composed about it.
 *
 * **Fetched at render time rather than read from a stored list.** A published
 * output is never copied into Midship and never registered as a document, so
 * there is no document path to reach one by: the manifest names the files and
 * the bytes come from a signed URL minted per request.
 *
 * **A spreadsheet opens where every other spreadsheet in Midship does.** A
 * `.csv` or workbook offers View beside Download, and the viewer is the same
 * one evidence opens in; a format Midship has no viewer for offers Download
 * alone rather than a View that would open onto "unsupported".
 *
 * **No row offers a star, not even a disabled one.** Inclusion in the workpaper
 * is a flag on a citation, and a citation anchors to a document — which a
 * published file is not. A disabled star would read as "not included yet" where
 * what is true is that there is nothing here to include.
 *
 * **Where no sample row is named every row keeps its buttons, disabled and
 * saying why.** The URL is minted against a row's run, so a pane open over a
 * whole column can ask for no file here. Rendering the buttons enabled spends
 * the auditor's click on nothing; dropping them reads as a file they are not
 * allowed to have, where what is true is that this pane is the wrong place to
 * ask from.
 *
 * Absent entirely when the run published nothing, which is the ordinary shape of
 * a run that failed before it got that far — and for that reason kept, with the
 * failure stated, when the manifest could not be read at all. The same absence
 * cannot serve for both, and the conclusion's callout reads the same list, so
 * the two agree about which of them is true.
 */
export function OutputFilesSection({
  outputs,
  unreadable,
  onView,
  onDownload,
  pending,
  fetchable,
  selectedFileId,
}: OutputFilesSectionProps) {
  if (unreadable) {
    return (
      <PaneSection title="OUTPUT FILES">
        <p className="text-sm text-font-secondary">
          The files this run published could not be read.
        </p>
      </PaneSection>
    );
  }

  if (outputs.length === 0) {
    return null;
  }

  return (
    <PaneSection title="OUTPUT FILES">
      <ul className="space-y-2">
        {outputs.map((output) => (
          // Marked current rather than its View pressed: pressing View again
          // reopens the file rather than closing it, so it is no toggle, and
          // on the sheet it opens a modal.
          <li
            key={output.fileId}
            aria-current={output.fileId === selectedFileId ? "true" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm",
              output.fileId === selectedFileId &&
                "border-stone-400 bg-stone-50",
            )}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate">{output.fileName}</div>
              {output.description !== null && (
                <div className="text-xs text-font-secondary">
                  {output.description}
                </div>
              )}
            </div>
            {/* The reason rides a wrapper and not the buttons it is about: a
                disabled button takes no pointer events, so a tooltip written
                on it never opens. It is said again in each accessible name,
                which is the only channel a `title` does not reach — a disabled
                control is announced by name and its absence of a reason cannot
                be hovered for. */}
            <span
              className="flex shrink-0 items-center"
              title={fetchable ? undefined : OUTPUT_FILE_NEEDS_A_ROW}
            >
              {viewableOutputFileType(output) !== null && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    fetchable
                      ? `View ${output.fileName}`
                      : `View ${output.fileName} — ${OUTPUT_FILE_NEEDS_A_ROW}`
                  }
                  disabled={!fetchable || pending.has(output.fileId)}
                  onClick={(e) => onView(output, e.currentTarget)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label={
                  fetchable
                    ? `Download ${output.fileName}`
                    : `Download ${output.fileName} — ${OUTPUT_FILE_NEEDS_A_ROW}`
                }
                disabled={!fetchable || pending.has(output.fileId)}
                onClick={() => onDownload(output)}
              >
                <Download className="h-4 w-4" />
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </PaneSection>
  );
}
